// PR-MAY16-RATE-DROPDOWN-FIX — rate sheet aligned to site_id.
//
// Yesterday's panel rendered ONLY rows that already had rates and
// asked the operator to pick from a "Site contract" dropdown. The
// dropdown showed nothing in dev because the JOIN went through a
// site_contracts table that wasn't always populated for the rate
// model the operator actually wanted.
//
// Today: the sheet shows ALL sites unconditionally (Amaala, Sharma,
// Trojena, Mina, NEOM Oxagon today). Each row shows either the live
// rate with an inline editor, or an "Add rate" affordance. There is
// no contract picker — rates are per site.

import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Edit2, Check, X, Plus, Layers, Copy } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  useInvestorSiteRates,
  useUpsertInvestorSiteRate,
  type InvestorSiteRateRow,
} from '@/hooks/useInvestorSiteRates';
import { extractErrorMessage, apiClient } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { BulkApplyDialog, CopyFromDialog } from './BulkRateActions';

interface InvestorRateSheetProps {
  investorId: string;
}

interface SiteLite { id: string; name_en: string; }

export function InvestorRateSheet({ investorId }: InvestorRateSheetProps) {
  const { t } = useTranslation();
  const { data: rates, isLoading, isError, error } = useInvestorSiteRates(investorId);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // All sites — feeds the rate-sheet rows (one row per site).
  // /api/v1/sites returns Prisma's camelCase nameEn; we normalise to
  // name_en here to keep the rate-row schema consistent with the
  // by-investor rate API (which is hand-SQL with snake_case aliases).
  const sitesQ = useQuery({
    queryKey: ['sites-list'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items?: Array<{ id: string; nameEn: string }> } | Array<{ id: string; nameEn: string }>>(
        `/api/v1/sites?pageSize=200`,
      );
      const items = Array.isArray(data) ? data : (data.items ?? []);
      return items.map((s) => ({ id: s.id, name_en: s.nameEn }));
    },
  });

  // Deep-link consumption — URL carries
  // ?section=rate-sheet[&siteId=…&vehicleId=…]. The site landing the
  // operator is sent to gets a highlight + auto-focus on its add-rate
  // cell.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightSiteId, setHighlightSiteId] = useState<string | null>(null);
  useEffect(() => {
    if (searchParams.get('section') !== 'rate-sheet') return;
    const sId = searchParams.get('siteId') ?? '';
    if (sId) setHighlightSiteId(sId);
    queueMicrotask(() => containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    const next = new URLSearchParams(searchParams);
    next.delete('section');
    next.delete('siteId');
    // vehicleId preserved for Vehicles section to consume.
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // PR-MAY16-RATE-SAVE-FIX — only consider rows that are CURRENTLY
  // ACTIVE (matching the resolver's filter): effective_from <= today
  // AND (effective_to IS NULL OR effective_to >= today). Yesterday's
  // backfilled rows have effective_to set in the past and are
  // intentionally treated as historical — they show "not set" until
  // a fresh active row is created (e.g. via the bulk-set in commit 2).
  const rateBySite = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const m = new Map<string, InvestorSiteRateRow>();
    for (const r of rates ?? []) {
      if (r.effective_from > today) continue;
      if (r.effective_to && r.effective_to < today) continue;
      const prev = m.get(r.site_id);
      if (!prev || r.effective_from > prev.effective_from) m.set(r.site_id, r);
    }
    return m;
  }, [rates]);

  if (isLoading || sitesQ.isLoading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive p-3 rounded bg-destructive/10 border border-destructive/30">
        {extractErrorMessage(error)}
      </p>
    );
  }

  const sites = sitesQ.data ?? [];

  return (
    <div className="space-y-3" data-investor-rate-sheet ref={containerRef}>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[420px]">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start px-3 py-2 font-medium">{t('investors.rateSheet.col.site')}</th>
              <th className="text-end px-3 py-2 font-medium">{t('investors.rateSheet.col.rate')}</th>
              <th className="text-start px-3 py-2 font-medium">{t('investors.rateSheet.col.from')}</th>
              <th className="px-2 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => (
              <SiteRow
                key={s.id}
                site={s}
                rate={rateBySite.get(s.id) ?? null}
                investorId={investorId}
                highlighted={s.id === highlightSiteId}
              />
            ))}
          </tbody>
        </table>
      </div>

      {flash && (
        <div className="text-xs p-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400" data-rate-sheet-flash>
          {flash}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-rate-sheet-bulk>
          <Layers className="h-3.5 w-3.5" />
          {t('investors.bulkApply.button')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setCopyOpen(true)} data-rate-sheet-copy>
          <Copy className="h-3.5 w-3.5" />
          {t('investors.copyFrom.button')}
        </Button>
      </div>

      {bulkOpen && (
        <BulkApplyDialog
          investorId={investorId}
          onClose={(r) => {
            setBulkOpen(false);
            if (r) setFlash(t('investors.bulkApply.flash', { created: r.created, skipped: r.skipped }) as string);
            setTimeout(() => setFlash(null), 4000);
          }}
        />
      )}
      {copyOpen && (
        <CopyFromDialog
          investorId={investorId}
          onClose={(r) => {
            setCopyOpen(false);
            if (r) setFlash(t('investors.copyFrom.flash', { created: r.created, updated: r.updated, skipped: r.skipped }) as string);
            setTimeout(() => setFlash(null), 4000);
          }}
        />
      )}
    </div>
  );
}

// One row per site. If a rate exists, the row shows it and offers an
// inline edit. If not, the row shows an "Add rate" input that creates
// the row when the operator commits a value.
function SiteRow({
  site, rate, investorId, highlighted,
}: {
  site: SiteLite;
  rate: InvestorSiteRateRow | null;
  investorId: string;
  highlighted: boolean;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState<string>(rate?.investor_rate_at_target ?? '');
  const [error, setError] = useState<string | null>(null);
  // PR-MAY16-RATE-SAVE-FIX — single upsert call replaces the
  // create/update fork. Always writes to today's active row.
  const upsertMut = useUpsertInvestorSiteRate(investorId);
  const isPending = upsertMut.isPending;

  // Open the editor automatically when this site was deep-linked.
  useEffect(() => {
    if (highlighted && !rate) setEditing(true);
  }, [highlighted, rate]);

  const commit = async () => {
    setError(null);
    const num = Number(val);
    if (!Number.isFinite(num) || num <= 0) {
      setError(t('investors.rateSheet.errors.invalidRate'));
      return;
    }
    try {
      await upsertMut.mutateAsync({
        siteId: site.id,
        investorId,
        investorRateAtTarget: num,
      });
      setEditing(false);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const rowCls = `border-t border-border/60 ${highlighted ? 'bg-amber-500/5' : ''}`;

  return (
    <tr className={rowCls} data-rate-sheet-row data-site-id={site.id}>
      <td className="px-3 py-2 font-medium text-foreground">{site.name_en}</td>
      <td className="px-3 py-2 text-end tabular-nums">
        {editing ? (
          <Input
            type="number" step="0.01" min="0" inputMode="decimal"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            className="h-8 w-28 text-end ms-auto"
            disabled={isPending}
            autoFocus
            data-rate-input
          />
        ) : rate ? (
          <span className="font-medium text-foreground">
            {Number(rate.investor_rate_at_target).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ) : (
          <span className="text-muted-foreground text-xs italic">{t('investors.rateSheet.notSet')}</span>
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground tabular-nums text-xs">
        {rate?.effective_from ?? '—'}
      </td>
      <td className="px-2 py-2 text-end">
        {editing ? (
          <div className="inline-flex items-center gap-1">
            <button type="button" onClick={commit} disabled={isPending}
                    className="text-emerald-500 hover:text-emerald-400" data-rate-commit>
              {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </button>
            <button type="button" onClick={() => { setEditing(false); setVal(rate?.investor_rate_at_target ?? ''); setError(null); }}
                    className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : rate ? (
          <button type="button" onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground" title="Edit">
            <Edit2 className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
                  data-add-rate>
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
        {error && (
          <div className="text-[10px] text-destructive text-end mt-0.5" role="alert">{error}</div>
        )}
      </td>
    </tr>
  );
}
